// src/components/DesignationList.tsx
import React from 'react';
import { Edit2, Trash2, Briefcase, Shield } from 'lucide-react';
import { Designation } from '../types/designation';

interface DesignationListProps {
  designations: Designation[];
  onEdit: (designation: Designation) => void;
  onDelete: (id: string) => void; // Changed from number to string for UUID
  loading?: boolean;
}

export function DesignationList({ designations, onEdit, onDelete, loading }: DesignationListProps) {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!designations || designations.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No designations found. Click "Add Designation" to create one.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {designations.map((desig) => {
        // Check if designation is a system default designation
        const isSystemDesignation = desig.is_default === true || desig.created_by === 'system';
        
        return (
          <div key={desig.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{desig.name || 'Untitled'}</h3>
                  {isSystemDesignation && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                      <Shield className="w-3 h-3" />
                      System Default
                    </span>
                  )}
                  {desig.is_active === false && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
                      Inactive
                    </span>
                  )}
                </div>
                {desig.description && (
                  <p className="text-sm text-gray-600">{desig.description}</p>
                )}
                <div className="flex gap-4 mt-2">
                  {desig.created_at && (
                    <p className="text-xs text-gray-400">
                      Created: {new Date(desig.created_at).toLocaleDateString()}
                    </p>
                  )}
                  {/* {desig.created_by && desig.created_by !== 'system' && (
                    <p className="text-xs text-gray-400">
                      Created by: {desig.created_by.substring(0, 8)}...
                    </p>
                  )} */}
                </div>
              </div>
              
              {/* Only show action buttons for non-system designations */}
              {!isSystemDesignation ? (
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(desig)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit designation"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => desig.id && onDelete(desig.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete designation"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center">
                  <span className="text-xs text-gray-400 italic flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    Protected
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}